import React, { useEffect, useState } from "react"
import { default as routes } from "api/routes"
import { SimpleForm, 
    TextInput, 
    PasswordInput,
    BooleanInput,
    SelectArrayInput,
    SelectInput,
    email,
    SaveButton, 
    DeleteButton,
    useRecordContext,
    required } from "react-admin"
import { AxiosRequestConfig } from "axios"
import { ScreenEmpty } from "components/ui/screen"
import { fetchWithToken } from "api/data"
import { useTranslate } from "ra-core"
import { Grid } from "@mui/material"

interface props {
    readonly: boolean
    newRecord: boolean
}

function Form(props: React.PropsWithChildren<props>): JSX.Element  {
    const { readonly, newRecord } = props
    const translate = useTranslate()
    const [profiles, setProfiles] = useState<{ id: string; name: string }[]>([])
    const record = useRecordContext()
    const [selectedProfiles, setSelectedProfiles] = useState<{ id: string; name: string }[]>([])

    const validateAtLeastOne = (value: any[]) => 
        value && value.length > 0 ? undefined : translate("validation.at_least_one")

    const handleProfilesChange = (event: any) => {
        const value = event.target ? event.target.value : event
        setSelectedProfiles(profiles.filter(prof => value.includes(prof.id)))
    }

    useEffect(() => {
        const config: AxiosRequestConfig = {
            url: routes.userProfileTypes(),
            method: 'get',
        }
        fetchWithToken(config)
            .then((response) => {
                setProfiles(response.data)
            })
            .catch((error) => console.error(error))
    }, [])

    useEffect(() => {
        if(record) {
            setSelectedProfiles(profiles.filter(prof => record.profiles?.includes(prof.id)))
        }
    }, [record, profiles])

    return (
        <SimpleForm toolbar={false}>
            <Grid container columnSpacing="8">
                <Grid item xs={4}>
                    <TextInput source="login" disabled={readonly} label={translate("resources.users.fields.login")}/>
                </Grid>
                <Grid item xs={4}>
                    <TextInput source="name" disabled={readonly} validate={[required()]} label={translate("resources.users.fields.name")}/>
                </Grid>
            </Grid>
            <Grid container columnSpacing="8">
                <Grid item xs={4}>
                    <TextInput source="email" disabled={readonly} validate={[required(), email()]} label={translate("resources.users.fields.email")}/>
                </Grid>
                <Grid item xs={4}>
                    <BooleanInput source="active" disabled={readonly} label={translate("resources.users.fields.active")}/>
                </Grid>
            </Grid>
            <Grid container columnSpacing="8">
                <Grid item xs={4}>
                    <SelectArrayInput 
                        source="profiles" 
                        choices={profiles}
                        disabled={readonly}
                        validate={[validateAtLeastOne]}
                        label={translate("resources.users.fields.profiles")}
                        onChange={(event) => handleProfilesChange(event)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInput 
                        validate={[required()]}
                        source="default_profile" 
                        choices={selectedProfiles}
                        disabled={readonly}
                        label={translate("resources.users.fields.current_profile")}
                    />
                </Grid>
            </Grid>
            <ScreenEmpty enabled={!readonly}>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <PasswordInput source="password" label={translate("resources.users.fields.password")}/>
                    </Grid>
                    <Grid item xs={4}>
                        <PasswordInput source="repeat_password" label={translate("resources.users.fields.repeat_password")}/>
                    </Grid>
                </Grid>
            </ScreenEmpty>
            <ScreenEmpty enabled={!readonly}>
                <Grid container>
                    <Grid item xs={6}>
                        <SaveButton />
                    </Grid>
                    <ScreenEmpty enabled={!newRecord}>
                        <Grid item xs={6}>
                            <DeleteButton />
                        </Grid>
                    </ScreenEmpty>
                </Grid>
            </ScreenEmpty>
        </SimpleForm>
    )
}

Form.defaultProps = {
    readonly: false,
    newRecord: false,
}

export default Form